import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getCOReturnCodeList } from "services/data.internal.service";
import { deactivateCOReturnCode } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { activateCOReturnCode } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getCOReturnCodeStatusCount } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function COReturnCodeList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["co-return-code-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Return Code"]) &&
      permissions["Return Code"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Return Code"]) &&
      permissions["Return Code"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Return Code"]) &&
      permissions["Return Code"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["co-return-code-list", { customerCode, active }],
    queryFn: () => getCOReturnCodeList({ active }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["co-return-code-list", "status", { customerCode }],
    queryFn: () => getCOReturnCodeStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateCOReturnCode, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activateCOReturnCode, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/setup/return-code/add");

  const onEdit = (req) => {
    navigate("/setup/return-code/edit", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.returnCode}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.returnCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Return Code", accessorKey: "returnCode" },
    { header: "Description 1", accessorKey: "description1" },
    { header: "Description 2", accessorKey: "description2" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { returnCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit return code"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ returnCode });
                }}
              />
              <DelButton
                title="deactivate return code"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ returnCode });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate return code"
              disabled={!editPermission}
              onClick={() => onActive({ returnCode })}
            />
          );
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;return&nbsp;code
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default COReturnCodeList;
